import React from 'react'

import {
   CodePenIcon,
   IconPropsType,
   IntersectCircleIcon,
   LayersTwoIcon,
   PuzzlePieceIcon,
   Rocket1Icon,
   RocketIcon2
} from '@flowwai-frontend/design-system/icons'
import { PipelineIcon } from '@flowwai-frontend/shared/sales-crm/sales-crm-core'
import { ColorVariantEnum } from '@flowwai-frontend/design-system'
import { colors } from '@flowwai-frontend/design-system'

import {
   PipelineIconColorsList,
   PipelineIconEnums,
   PipelineIconEnumsList,
   PipelineIconsColors
} from '../constants/PipelineIconsConstants'

const hashString = (inputString: string) =>
   inputString
      .split('')
      .map(char => char.charCodeAt(0))
      .reduce((a, b) => a + b, 0)

const selectPipelineIconEnum = (pipelineId: string) =>
   PipelineIconEnumsList[hashString(pipelineId) % PipelineIconEnumsList.length]

const selectPipelineIconColor = (pipelineId: string) =>
   PipelineIconColorsList[
      hashString(pipelineId) % PipelineIconColorsList.length
   ]

export const renderPipelineIcon = (
   pipelineId: string,
   pipelineIcon?: PipelineIcon,
   iconProps?: IconPropsType
) => {
   const areIconDetailsAvailable =
      !!pipelineIcon?.iconId && !!pipelineIcon?.iconColor

   if (areIconDetailsAvailable) {
      const color = getColorFromColorVariantEnum(
         pipelineIcon.iconColor as ColorVariantEnum
      )

      return (
         <svg
            width={iconProps?.width ?? 20}
            height={iconProps?.height ?? 20}
            fill={color}
            stroke={color}
         >
            <use
               xlinkHref={`/assets/icons/iconPicker.svg#${pipelineIcon.iconId}`}
            />
         </svg>
      )
   }

   const pipelineIconEnum = selectPipelineIconEnum(pipelineId)
   const pipelineIconColors =
      PipelineIconsColors[selectPipelineIconColor(pipelineId)]

   switch (pipelineIconEnum) {
      case PipelineIconEnums.CodePen:
         return (
            <CodePenIcon
               width={20}
               height={20}
               fill={pipelineIconColors.backgroundColor}
               {...iconProps}
            />
         )
      case PipelineIconEnums.InterSectCircle:
         return (
            <IntersectCircleIcon
               width={20}
               height={20}
               fill={pipelineIconColors.backgroundColor}
               {...iconProps}
            />
         )
      case PipelineIconEnums.Rocket1:
         return (
            <Rocket1Icon
               width={20}
               height={20}
               fill={pipelineIconColors.backgroundColor}
            />
         )
      case PipelineIconEnums.Rocket2:
         return (
            <RocketIcon2
               width={20}
               height={20}
               fill={pipelineIconColors.backgroundColor}
               {...iconProps}
            />
         )
      case PipelineIconEnums.LayersTwo:
         return (
            <LayersTwoIcon
               width={20}
               height={20}
               fill={pipelineIconColors.backgroundColor}
               {...iconProps}
            />
         )
      case PipelineIconEnums.PuzzlePiece:
         return (
            <PuzzlePieceIcon
               width={20}
               height={20}
               fill={pipelineIconColors.backgroundColor}
               {...iconProps}
            />
         )
      default:
         return <></>
   }
}

export const getColorFromColorVariantEnum = (
   color: ColorVariantEnum
): string => {
   switch (color) {
      case 'BLUE':
         return colors['blue-600']
      case 'BLUE_DARK':
         return colors['blue-dark-600']
      case 'BLUE_LIGHT':
         return colors['blue-light-600']
      case 'CYAN':
         return colors['cyan-600']
      case 'ERROR':
         return colors['error-600']
      case 'FUCHSIA':
         return colors['fuchsia-600']
      case 'GRAY':
         return colors['gray-600']
      case 'GREEN':
         return colors['green-600']
      case 'GREEN_LIGHT':
         return colors['green-light-600']
      case 'INDIGO':
         return colors['indigo-600']
      case 'MOSS':
         return colors['moss-600']
      case 'ORANGE':
         return colors['orange-600']
      case 'ORANGE_DARK':
         return colors['orange-dark-600']
      case 'PINK':
         return colors['pink-600']
      case 'PRIMARY':
         return colors['primary-600']
      case 'PURPLE':
         return colors['purple-600']
      case 'ROSE':
         return colors['rose-600']
      case 'SUCCESS':
         return colors['success-600']
      case 'TEAL':
         return colors['teal-600']
      case 'VIOLET':
         return colors['violet-600']
      case 'WARNING':
         return colors['warning-600']
      case 'YELLOW':
         return colors['yellow-600']
      default: {
         return colors['primary-600']
      }
   }
}
